import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import * as Collapsible from '@radix-ui/react-collapsible';
import { ChevronDown, ChevronRight, FileText, HelpCircle, Package, Tag as TagIcon } from 'lucide-react';
import type { TreeResponse, ViewNodeDto } from '../lib/api';

/**
 * Tag-derived navigation.
 *
 * The hierarchy is Program → Subproject → Section, materialized from tags rather than
 * from where a file happens to live. A document carrying two subprojects genuinely
 * appears under both; that is not duplication, because there is still exactly one
 * canonical document behind every appearance.
 */

const ICONS: Record<ViewNodeDto['kind'], JSX.Element> = {
  program: <TagIcon size={12} />,
  subproject: <Package size={12} />,
  section: <FileText size={12} />,
  document: <FileText size={12} />,
  unclassified: <HelpCircle size={12} />,
};

export function CatalogTree({
  tree,
  selectedPath,
  selectedTags,
  onToggleTag,
  sort,
  onSortChange,
  loading,
}: {
  tree: TreeResponse | null;
  selectedPath: string | null;
  selectedTags: string[];
  onToggleTag: (tagId: string) => void;
  sort: string;
  onSortChange: (sort: string) => void;
  loading: boolean;
}) {
  return (
    <nav className="doc-index virtual-tree-container" aria-label="Catalog">
      <div className="tree-header">
        <h3>Catalog</h3>
        <div className="tree-controls">
          <select
            className="sort-select"
            title="Sort order"
            aria-label="Sort order"
            value={sort}
            onChange={(event) => onSortChange(event.target.value)}
          >
            <option value="relevance">Relevance</option>
            <option value="recent">Recent</option>
            <option value="citation">Citation</option>
            <option value="title">Title</option>
          </select>
        </div>
      </div>

      {loading ? <div className="tree-msg">Loading catalog…</div> : null}

      {/*
        The applied logic is stated in words. Same namespace widens, different
        namespaces narrow -- a reader should not have to infer that from results.
      */}
      {selectedTags.length > 0 && tree ? (
        <div className="active-filters">
          <div className="filter-logic">Match: {tree.filter.description}</div>
          <div className="filter-chips">
            {tree.filter.groups.flatMap((group) =>
              group.tags.map((tag) => (
                <span key={tag.id} className={`tag-chip namespace-${group.namespace}`}>
                  {group.namespace}: {tag.displayName}
                  <button type="button" aria-label={`Remove ${tag.displayName}`} onClick={() => onToggleTag(tag.id)}>
                    ×
                  </button>
                </span>
              )),
            )}
          </div>
        </div>
      ) : null}

      {!loading && tree && tree.nodes.length === 0 ? (
        <div className="tree-msg">No documents matched these tags.</div>
      ) : null}

      <ul className="virtual-tree" role="tree">
        {tree?.nodes.map((node) => (
          <TreeNode
            key={node.id}
            node={node}
            depth={0}
            selectedPath={selectedPath}
            onToggleTag={onToggleTag}
            selectedTags={selectedTags}
          />
        ))}
      </ul>
    </nav>
  );
}

function TreeNode({
  node,
  depth,
  selectedPath,
  selectedTags,
  onToggleTag,
}: {
  node: ViewNodeDto;
  depth: number;
  selectedPath: string | null;
  selectedTags: string[];
  onToggleTag: (tagId: string) => void;
}) {
  // Deep branches stay closed so a large corpus opens responsively.
  const [open, setOpen] = useState(depth < 2);
  const isDocument = node.kind === 'document';
  const active = isDocument && node.sourcePath === selectedPath;
  const filtered = node.tagId !== undefined && selectedTags.includes(node.tagId);

  if (isDocument) {
    return (
      <li className={`tree-node${active ? ' active' : ''}`} role="treeitem" aria-selected={active}>
        <Link
          to="/files/$"
          params={{ _splat: node.sourcePath ?? '' }}
          className="tree-item"
          style={{ textDecoration: 'none' }}
        >
          <span className="tree-icon" aria-hidden="true">
            {ICONS.document}
          </span>
          {node.label}
        </Link>
      </li>
    );
  }

  return (
    <li className={`tree-node${open ? ' expanded' : ' collapsed'}`} role="treeitem" aria-expanded={open}>
      <Collapsible.Root open={open} onOpenChange={setOpen}>
        <div className="tree-item">
          <Collapsible.Trigger asChild>
            <button
              type="button"
              className="tree-caret"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              aria-label={open ? `Collapse ${node.label}` : `Expand ${node.label}`}
            >
              {open ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
            </button>
          </Collapsible.Trigger>
          <span className="tree-icon" aria-hidden="true">
            {ICONS[node.kind]}
          </span>
          {node.tagId ? (
            <button
              type="button"
              onClick={() => onToggleTag(node.tagId as string)}
              aria-pressed={filtered}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: 'inherit',
                font: 'inherit',
                fontWeight: filtered ? 700 : undefined,
              }}
            >
              {node.label}
            </button>
          ) : (
            <span>{node.label}</span>
          )}
          <span className="relation" style={{ marginLeft: 'auto' }}>
            {node.documentCount}
          </span>
        </div>
        <Collapsible.Content asChild>
          <ul className="tree-children" role="group">
            {node.children.map((child) => (
              <TreeNode
                key={child.id}
                node={child}
                depth={depth + 1}
                selectedPath={selectedPath}
                selectedTags={selectedTags}
                onToggleTag={onToggleTag}
              />
            ))}
          </ul>
        </Collapsible.Content>
      </Collapsible.Root>
    </li>
  );
}
